import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CheckBox,
  Icon,
  Input,
  Pagination,
  Select,
  SelectOption,
  Spinner,
  Table,
  TableData,
  TableHeader,
  Text,
} from '@fairfashion/design-system'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { GlobalDataContext } from '../../../../contexts/globalData'
import { useDebounceValue } from '../../../../hooks/useDebounceValue'
import { useInViewport } from '../../../../hooks/useInViewport'
import {
  IIntegrationStatus,
  IRankedProviderDTO,
} from '../../../../sdk/company/company.types'
import { sdk } from '../../../../services/sdk'
import { IPaginatedResponse } from '../../../../types/paginated-response.type'
import { docMask } from '../../../../utilities/masks/brasilianDocuments'
import { ProvidersContext } from '../../providers.context'

import * as Styles from './providers-table.styles'
import { FilterProps } from './providers-table.types'

export const ProvidersTable = () => {
  const { ref } = useInViewport()
  const navigate = useNavigate()
  const theme = useTheme()
  const { companySegmentType } = useContext(GlobalDataContext)
  const { actions, checkboxRisksFilter } = useContext(ProvidersContext)
  const [filteredData, setFilteredData] = useState<IPaginatedResponse<
    IRankedProviderDTO[]
  > | null>(null)
  const [segmentsOptions, setSegmentsOptions] = useState<SelectOption[]>([])
  const [tableLoading, setTableLoading] = useState(false)
  const [filters, setFilters] = useState<FilterProps>({
    page: 1,
    search: undefined,
    status: undefined,
    segment: undefined,
    existSocialEnvironmentalRisk: checkboxRisksFilter,
    integrationStatus: undefined,
  })
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounceValue(searchInput, 500)

  const fetchSegments = useCallback(async () => {
    const response = await sdk.company.getSegments()
    setSegmentsOptions(
      response.map((item) => ({ value: item.name, label: item.translate })),
    )
  }, [])

  useEffect(() => {
    fetchSegments()
  }, [])

  useEffect(() => {
    setFilters({
      ...filters,
      existSocialEnvironmentalRisk: checkboxRisksFilter,
    })
  }, [checkboxRisksFilter])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearch || undefined,
      page: 1,
    }))
  }, [debouncedSearch])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableLoading(true)
        const filterData = await sdk.company.getRankedProviders(
          filters.page,
          filters.search,
          filters.status,
          filters.segment,
          filters.existSocialEnvironmentalRisk,
          filters.integrationStatus,
        )
        setFilteredData(filterData)
      } catch (error) {
      } finally {
        setTableLoading(false)
      }
    }
    fetchData()
  }, [filters])

  const headers: TableHeader[] = [
    {
      key: 'existSocialEnvironmentalRisk',
      label: 'Representatividade',
      width: '150px',
      customRender: (dataRow) => {
        return (
          <Box flex={1} justifyContent="center" alignItems="center" gap={3}>
            <Text size="small" color="grey_3">
              {dataRow.representation}%
            </Text>
            <Icon
              name="flag"
              size={22}
              color={
                dataRow.socialEnvironmentalRisks?.length > 0
                  ? 'warning'
                  : 'grey_4'
              }
            />
          </Box>
        )
      },
    },
    {
      key: 'name',
      label: 'Nome do fornecedor',
      minWidth: '300px',
      customRender: (dataRow) => {
        return (
          <Styles.CustomText>
            <Text size="small">{dataRow.name}</Text>
          </Styles.CustomText>
        )
      },
    },
    {
      key: 'taxId',
      label: 'CNPJ',
      customRender: (dataRow) => {
        return <Text size="small">{docMask(dataRow.taxId ?? '')}</Text>
      },
    },
    {
      key: 'segment',
      label: 'Segmento',
      customRender: (dataRow) => {
        return (
          <Text size="small">
            {
              companySegmentType?.[
                dataRow.segment as keyof typeof companySegmentType
              ]?.translate
            }
          </Text>
        )
      },
    },
    {
      key: 'lastDeclaration',
      label: 'Última declaração',
      customRender: (dataRow) => {
        return (
          <Text
            size="small"
            color={dataRow.declarationIsExpired ? 'error' : 'grey_3'}
          >
            {dataRow.lastDeclarationTo && dataRow.lastDeclarationFrom
              ? `${moment(dataRow.lastDeclarationFrom).format('DD/MM/yyyy')} - ${moment(dataRow.lastDeclarationTo).format('DD/MM/yyyy')}`
              : ''}
          </Text>
        )
      },
    },
    {
      key: 'integration',
      label: 'Integração',
      customRender: (dataRow) => {
        return (
          <Styles.Badge
            active={
              dataRow.integrationStatus === IIntegrationStatus.INTEGRATED ||
              dataRow.integrationStatus === IIntegrationStatus.SENT_DECLARATION
            }
          >
            {dataRow.integrationStatus === IIntegrationStatus.INTEGRATED &&
              'Integrado'}
            {dataRow.integrationStatus ===
              IIntegrationStatus.SENT_DECLARATION && 'Declarado'}
            {dataRow.integrationStatus === IIntegrationStatus.INACTIVE &&
              'Inativo'}
          </Styles.Badge>
        )
      },
    },
    {
      key: 'status',
      label: 'Status',
      customRender: (dataRow) => {
        return (
          <Styles.Badge active={dataRow.status === 'active'}>
            {dataRow.status === 'active' && 'Ativo'}
            {dataRow.status === 'inactive' && 'Inativo'}
          </Styles.Badge>
        )
      },
    },
    {
      key: 'showMore',
      label: 'Ver',
      width: '50px',
      customRender: (dataRow) => {
        if (!dataRow.taxId) {
          return <></>
        }

        return (
          <Button
            onClick={() => navigate(`/providers/${dataRow.taxId}`)}
            variant="text"
            padding="0"
          >
            <Icon name="user_search" size={22} color={'secondary'} />
          </Button>
        )
      },
    },
  ]

  const tableData = useMemo(() => {
    if (!filteredData?.data?.length) return [] as TableData[]

    return filteredData.data.map((item) => {
      const _data: TableData = {
        ...item,
      }
      if (item.socialEnvironmentalRisk?.length > 0) {
        _data.customRender = (
          dataRow: IRankedProviderDTO,
          index: number,
          defaultRender: (row: any, index: number) => JSX.Element,
        ) => (
          <Styles.CustomRowContainer width="100%" key={`custom-row-${index}`}>
            {defaultRender(dataRow, index)}
          </Styles.CustomRowContainer>
        )
      }
      return _data
    })
  }, [filteredData?.data]) as TableData[]

  return (
    <Styles.ProvidersTableContainer
      direction="column"
      width="100%"
      gap={4}
      id="providerTable"
    >
      <Box
        flex={1}
        direction="column"
        width="100%"
        backgroundColor={theme.colors.custom.white}
        borderRadius={theme.border_radius.box}
        border={{
          color: 'grey_5',
          width: 1,
        }}
        boxShadow={theme.box_shadow.card}
      >
        <Box
          justifyContent="space-between"
          width="100%"
          flexWrap="wrap"
          padding={2}
          gap={2}
        >
          <Box width="300px">
            <Input
              adornment={<Icon name="search" />}
              placeholder="Buscar fornecedor"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
              }}
            />
          </Box>
          <Box alignItems="center" gap={2} flexWrap="wrap">
            <Box>
              <CheckBox
                checked={checkboxRisksFilter}
                onChange={() =>
                  actions.setCheckboxRisksFilter(!checkboxRisksFilter)
                }
                label="Fornecedores com riscos"
              />
            </Box>
            <Select
              width="200px"
              placeholder="Segmento"
              options={[{ value: 'all', label: 'Todos' }, ...segmentsOptions]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  segment:
                    e.target.value === 'all' ? undefined : e.target.value,
                })
              }
            />
            <Select
              width="150px"
              placeholder="Status"
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'active', label: 'Ativo' },
                { value: 'inactive', label: 'Inativo' },
              ]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value === 'all' ? undefined : e.target.value,
                })
              }
            />
            <Select
              width="250px"
              placeholder="Status da integração"
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'INTEGRATED', label: 'Integrado' },
                { value: 'SENT_DECLARATION', label: 'Declarado' },
                { value: 'INACTIVE', label: 'Inativo' },
              ]}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  integrationStatus:
                    e.target.value === 'all' ? undefined : e.target.value,
                })
              }
            />
          </Box>
        </Box>
        <Box width="100%" ref={ref}>
          {tableLoading ? (
            <Box
              width="100%"
              height="100%"
              padding={3}
              alignItems="center"
              minHeight="430px"
              justifyContent="center"
            >
              <Spinner size="40px" />
            </Box>
          ) : (
            <Table
              headers={headers}
              data={tableData}
              borderRadius={`0 0 ${theme.border_radius.box} ${theme.border_radius.box}`}
              footer={
                <Box justifyContent="center" width="100%" paddingY={1}>
                  {filteredData && filteredData?.count > 0 ? (
                    <Pagination
                      total={Math.ceil(filteredData.count / 5)}
                      current={filters?.page || 1 >= 1 ? filters.page : 1}
                      onChange={(_page) =>
                        setFilters({
                          ...filters,
                          page: _page,
                        })
                      }
                    />
                  ) : (
                    <Box padding={6}>
                      <Text>Não há dados</Text>
                    </Box>
                  )}
                </Box>
              }
            />
          )}
        </Box>
      </Box>
    </Styles.ProvidersTableContainer>
  )
}
